import type { Route, RouteConfig } from 'vue-router';
import { entityDetailRoute } from '@redviking/argonaut-core-ui/src/components/EntityDetail/entity-route-util';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import {
    getAppletDesignVersion,
    saveAppletDesignVersion,
} from './applet.entity';
import {
    appletVariablesTabRoute,
    eventsTabRoute,
    flowsTabRoute,
    generalTabRoute,
    jsonTabRoute,
    screensTabRoute,
    scriptsTabRoute,
} from './util/consts';
import { getAppletDiffereneces } from './getAppletDesignChanges';
import { validateAcls } from 'src/auth/validations';

export const appletDesignRoutes: RouteConfig[] = [
    {
        path: '',
        name: 'appletDesignList',
        component: () => import(/* webpackChunkName: "appletDesign" */ './AppletDesignList.view.vue'),
        meta: {
            pageBannerTitle: 'Applet Designs',
        },
    },
    entityDetailRoute<'appletDesignVersion'>({
        path: ':appletDesignId/version/:appletDesignVersionId',
        name: 'appletDesignVersion',
        component: () => import(/* webpackChunkName: "appletDesign" */ './AppletDesignVersion.view.vue'),
        meta: {
            useEntityPadding: false,
            useEntityDetailTabs: false,
            canEdit: () => {
                const adv = accessor.entityAsType('appletDesignVersion');
                return adv?.status === 'staged' && validateAcls([ 'applet_manager' ]);
            },
            entityType: 'appletDesignVersion',
            getEntity: getAppletDesignVersion,
            saveEntity: saveAppletDesignVersion,
            preSaveMaintenance: accessor => {
                accessor.appletDesign.resetInvalidNodeControls();
                accessor.appletDesign.resetInvalidNodes();
                accessor.appletDesign.resetInvalidConnections();
                accessor.appletDesign.setValidatingScripts(true);
            },
            entityChangesDetail: {
                getChanges: getAppletDiffereneces,
                changesCategoryComponent: () => import(/* webpackChunkName: "material" */ './currentChanges/AppletDesignCurrentChangesInstance.vue').then(m => m.default),
            },
            pageBannerTitle: (route: Route) => {
                const mode = route.query?.mode || '';
                const cfgName = accessor.entityAsType('appletDesignVersion')?.design?.name || '';
                const cfgVerNum = accessor.entityAsType('appletDesignVersion')?.versionNumber;
                if (mode === 'create' && !cfgName) {
                    return 'New Applet Design';
                }
                return cfgVerNum ? `${cfgName} v${cfgVerNum}` : cfgName;
            },
            returnRoute: { name: 'appletDesignList' },
        },
        redirect: to => ({ name: generalTabRoute, params: to.params }),
        children: [
            {
                path: 'general',
                name: generalTabRoute,
                component: () => import(/* webpackChunkName: "appletDesign" */ './General.tab.view.vue'),
                meta: {
                    tab: {
                        label: 'General',
                        icon: 'mdi-note-text',
                    },
                },
            },
            {
                path: 'applet-variables',
                name: appletVariablesTabRoute,
                components: {
                    default: () => import(/* webpackChunkName: "appletDesign" */ './AppletVariables.tab.vue'),
                },
                meta: {
                    tab: {
                        icon: 'mdi-variable',
                        label: 'Applet Variables',
                    },
                },
            },
            {
                path: 'screens',
                name: screensTabRoute,
                components: {
                    default: () => import(/* webpackChunkName: "appletDesign" */ './screens/Screen.tab.view.vue'), // a component for the settings page content
                },
                meta: {
                    tab: {
                        label: 'Screens',
                        icon: 'mdi-monitor-dashboard',
                    },
                },
            },
            {
                path: 'events',
                name: eventsTabRoute,
                components: {
                    default: () => import(/* webpackChunkName: "appletDesign" */ './events/Events.tab.view.vue'),
                },
                meta: {
                    tab: {
                        label: 'Events',
                        icon: 'mdi-flag',
                    },
                },
            },
            {
                path: 'flows',
                name: flowsTabRoute,
                components: {
                    default: () => import(/* webpackChunkName: "appletDesign" */ './flows/Flow.tab.view.vue'),
                },
                meta: {
                    tab: {
                        label: 'Flows',
                        icon: 'mdi-share-variant',
                    },
                },
            },
            {
                path: 'json',
                name: jsonTabRoute,
                components: {
                    default: () => import(/* webpackChunkName: "appletDesign" */ './Json.tab.view.vue'),
                },
                meta: {
                    tab: {
                        label: 'JSON',
                        icon: 'mdi-code-braces',
                    },
                },
            },
            {
                path: 'scripts',
                name: scriptsTabRoute,
                components: {
                    default: () => import(/* webpackChunkName: "appletDesign" */ './Script.tab.view.vue'),
                },
            },
        ],
    }),
];
